import { useEffect } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { useChargePointsStore } from '@/features/charge-points/store/charge-points-store';
import { useFilterStore } from '@/features/filters/store/filter-store';

// Screen areas covered by UI (px); keep in sync with the layouts
const DESKTOP_SIDEBAR = 400; // left sidebar 384px + 16px edge
const DESKTOP_PANEL = 436; // details panel 420px + 16px edge
const MOBILE_TOP_UI = 200; // logo row + search + chips
const MOBILE_BOTTOM_UI = 280; // charger cards (details sheet opens on demand)
const MARGIN = 72;

export interface Area {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/** Part of the map (in map-div pixels) not covered by panels and controls */
export function visibleArea(W: number, H: number, hasSidebar: boolean): Area {
  if (W >= 800) {
    return {
      left: (hasSidebar ? DESKTOP_SIDEBAR : 0) + MARGIN,
      right: W - DESKTOP_PANEL - MARGIN,
      top: MARGIN,
      bottom: H - MARGIN,
    };
  }
  return {
    left: MARGIN / 2,
    right: W - MARGIN / 2,
    top: MOBILE_TOP_UI,
    bottom: H - MOBILE_BOTTOM_UI,
  };
}

/**
 * Screen offset to pan by so a pin at (x, y) lands in the middle of the
 * visible area, or null when it's already inside it.
 */
export function panOffset(
  x: number,
  y: number,
  W: number,
  H: number,
  area: Area
): { dx: number; dy: number } | null {
  if (x >= area.left && x <= area.right && y >= area.top && y <= area.bottom) {
    return null;
  }
  return {
    dx: W / 2 - (area.left + area.right) / 2,
    dy: H / 2 - (area.top + area.bottom) / 2,
  };
}

/**
 * When the selection changes, pan the map only if the selected pin is hidden
 * behind the sidebar, details panel / sheet or too close to an edge - so
 * clicking a visible pin doesn't move the map under the cursor.
 */
export function useKeepSelectionInView() {
  const map = useMap();
  const hasSidebar = useFilterStore((s) => s.controlsEnabled);
  const selectedId = useChargePointsStore((s) => s.selectedChargePoint?.id);
  const lat = useChargePointsStore(
    (s) => s.selectedChargePoint?.coordinate.lat
  );
  const lng = useChargePointsStore(
    (s) => s.selectedChargePoint?.coordinate.lng
  );

  useEffect(() => {
    if (!map || !selectedId || lat === undefined || lng === undefined) return;
    const projection = map.getProjection();
    const zoom = map.getZoom();
    const center = map.getCenter();
    if (!projection || zoom === undefined || !center) {
      map.panTo({ lat, lng });
      return;
    }

    const { width: W, height: H } = map.getDiv().getBoundingClientRect();
    const scale = 2 ** zoom;
    const c = projection.fromLatLngToPoint(center);
    const p = projection.fromLatLngToPoint({ lat, lng });
    if (!c || !p) return;
    const offset = panOffset(
      (p.x - c.x) * scale + W / 2,
      (p.y - c.y) * scale + H / 2,
      W,
      H,
      visibleArea(W, H, hasSidebar)
    );
    if (!offset) return;

    const target = projection.fromPointToLatLng(
      new google.maps.Point(p.x + offset.dx / scale, p.y + offset.dy / scale)
    );
    if (target) map.panTo(target);
    // Only on selection change - detail refetches keep the same id/coords
  }, [map, selectedId, lat, lng, hasSidebar]);
}
